import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { ChevronRight, User, Clock, Info, Bell, LogOut, FileText, Users, Megaphone, Navigation, Menu, Archive, Download, RefreshCw, Key, Settings } from 'lucide-react';
import { spacing, fontSize, fontWeight } from '../../styles/theme';
import { usePermissions } from '../hooks/usePermissions';
import { useSession } from '../../contexts/SessionContext';
import { useTheme } from '../../contexts/ThemeContext';
import packageJson from '../../../package.json';
import formatUsername from '../lib/formatUsername';
import NotificationService from '../services/NotificationService';
import SoundService from '../services/SoundService';

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  height: 100vh;
  background-color: ${p => p.theme.colors.background};
  overflow: hidden;
`;

const Content = styled.div`
  height: calc(100vh - 52px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 8px 16px 60px;

  @media (max-width: 768px) {
    padding: 8px 12px 60px;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: ${fontWeight.bold};
  color: ${p => p.theme.colors.text};
  margin: 0 0 16px;
  padding-top: ${spacing.xl};
  letter-spacing: -0.02em;
`;

const CardGroup = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 22px;
  overflow: hidden;
  border: 1px solid ${p => p.theme.mode === 'light' ? p.theme.colors.border : 'transparent'};
  margin-bottom: 14px;
`;

const Divider = styled.div`
  height: 1px;
  background: ${p => p.theme.mode === 'light' ? p.theme.colors.border : 'rgba(255,255,255,0.05)'};
  margin-left: 52px;
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  padding: 13px 16px;
  background: none;
  border: none;
  cursor: ${p => p.$static ? 'default' : 'pointer'};
  width: 100%;
  text-align: left;
  transition: background-color 0.15s ease;
  &:hover {
    background: ${p => p.$static ? 'transparent' : p.theme.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)'};
  }
  &:active { background: ${p => p.$static ? 'transparent' : p.theme.mode === 'light' ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}; }
`;

const IconBox = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;
  background: ${p => p.$bg || 'rgba(0,0,0,0.08)'};
  color: ${p => p.$color || p.theme.colors.text};
`;

const RowLabel = styled.div`
  flex: 1;
  font-size: 15px;
  font-weight: 400;
  color: ${p => p.$red ? '#ef4444' : p.theme.colors.text};
  letter-spacing: -0.01em;
`;

const RowValue = styled.div`
  font-size: 14px;
  color: ${p => p.theme.colors.muted};
  margin-right: 6px;
  opacity: 0.5;
`;

const Chevron = styled.div`
  color: ${p => p.theme.colors.muted};
  opacity: 0.25;
`;

const AccountAvatar = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: ${p => p.theme.mode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
  color: ${p => p.theme.colors.text};
`;

const ToggleTrack = styled.div`
  width: 44px;
  height: 26px;
  border-radius: 999px;
  background: ${p => p.$on ? '#22c55e' : p.theme.mode === 'light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.12)'};
  position: relative;
  transition: background 0.2s ease;
  flex-shrink: 0;
  cursor: pointer;
`;

const ToggleThumb = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: white;
  position: absolute;
  top: 3px;
  left: ${p => p.$on ? '21px' : '3px'};
  transition: left 0.2s ease;
  box-shadow: 0 1px 4px rgba(0,0,0,0.3);
`;

const StandaloneCard = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  padding: 16px;
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.$red ? 'rgba(239,68,68,0.15)' : p.theme.mode === 'light' ? p.theme.colors.border : 'transparent'};
  border-radius: 22px;
  cursor: pointer;
  margin-bottom: 14px;
  text-align: left;
  transition: opacity 0.15s;
  &:hover { opacity: 0.8; }
`;

// Overlay / Modal
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Modal = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 18px;
  max-width: 380px;
  width: 90%;
  padding: 24px 20px 20px;
  box-shadow: 0 32px 80px rgba(0,0,0,0.4);
  border: 1px solid ${p => p.theme.colors.border};
`;

const ModalTitle = styled.h2`
  margin: 0 0 8px;
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.bold};
  color: ${p => p.theme.colors.text};
`;

const ModalText = styled.p`
  margin: 0 0 20px;
  font-size: 15px;
  color: ${p => p.theme.colors.muted};
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ModalBtn = styled.button`
  border: none;
  border-radius: 999px;
  padding: 9px 20px;
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  font-size: ${fontSize.sm};
  background: ${p => p.$danger ? '#ef4444' : p.theme.colors.border};
  color: ${p => p.$danger ? '#fff' : p.theme.colors.text};
  transition: filter 0.15s ease;
  &:hover { filter: brightness(0.92); }
`;

const NavOption = styled.button`
  width: 100%;
  background: ${p => p.$active ? p.theme.colors.border : 'transparent'};
  border: none;
  border-radius: 10px;
  padding: 13px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${p => p.theme.colors.text};
  font-size: 15px;
  cursor: pointer;
  text-align: left;
  &:active { background: ${p => p.theme.colors.border}; }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

const Toast = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  padding: 10px 18px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  animation: ${slideDown} 0.25s ease;
  background: ${p => p.$type === 'error' ? '#ef4444' : '#16a34a'};
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function SettingsScreen() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { logout } = useSession();
  const [showLogout, setShowLogout] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return JSON.parse(localStorage.getItem('kmfc-sound-enabled') || 'false'); } catch { return false; }
  });
  const { themePref: theme, themeMode, setTheme: setThemeCtx } = useTheme();
  const [navApp, setNavApp] = useState(() => localStorage.getItem('kmfc-nav-app') || 'apple');
  const [showNavPicker, setShowNavPicker] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [toast, setToast] = useState(null);
  const [showArchive, setShowArchive] = useState(false);

  const stored = (() => { try { return JSON.parse(localStorage.getItem('kmfc_user') || '{}'); } catch { return {}; } })();
  const username = formatUsername(stored.username) || '—';
  const roleId = stored.role_id;
  const roleLabel = { 1: 'Ontwikkelaar', 2: 'Eigenaar', 3: 'Medewerker' }[roleId] || '';
  const roleDisplay = `KMFC Shop${roleLabel ? ` — ${roleLabel}` : ''}`;

  const canManage = hasPermission('can_manage_settings');
  const canManageUsers = roleId === 1 || roleId === 2;
  const version = `V${packageJson.version}`;

  const NAV_OPTIONS = [
    { id: 'apple', label: 'Apple Maps' },
    { id: 'google', label: 'Google Maps' },
    { id: 'waze', label: 'Waze' },
  ];
  const THEME_OPTIONS = [
    { id: 'system', label: 'Systeem' },
    { id: 'dark', label: 'Donker' },
    { id: 'light', label: 'Licht' },
  ];
  const navLabel = NAV_OPTIONS.find(o => o.id === navApp)?.label || 'Apple Maps';
  const themeLabel = THEME_OPTIONS.find(o => o.id === theme)?.label || 'Systeem';

  const toggleSound = async () => {
    const ns = NotificationService.getInstance();
    const next = !soundEnabled;
    await ns.setSoundEnabled(next);
    setSoundEnabled(next);
    SoundService.reEvaluate();
  };

  const selectNavApp = (id) => {
    setNavApp(id);
    localStorage.setItem('kmfc-nav-app', id);
    setShowNavPicker(false);
  };

  const performLogout = () => {
    try { logout(); } catch { localStorage.clear(); window.location.href = 'https://mijn.webvonk.com'; }
  };

  const iconStyle = (color) => ({
    bg: `${color}1f`,
    color,
  });

  const gray = iconStyle(themeMode === 'light' ? '#6b7280' : '#9ca3af');
  const primary = { bg: themeMode === 'light' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.1)', color: themeMode === 'light' ? '#111' : '#fff' };

  return (
    <Container>
      <Content>
        <PageTitle>Instellingen</PageTitle>

        {/* Account */}
        <CardGroup>
          <Row onClick={() => navigate('/KMFC/settings/account')}>
            <AccountAvatar><User size={20} /></AccountAvatar>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: 'inherit', letterSpacing: '-0.01em' }}>{username}</div>
              <div style={{ fontSize: 13, marginTop: 3, opacity: 0.5 }}>{roleDisplay}</div>
            </div>
            <Chevron><ChevronRight size={16} /></Chevron>
          </Row>
        </CardGroup>

        {/* Beveiliging */}
        {canManage && (
          <CardGroup>
            <Row onClick={() => navigate('/KMFC/settings/account')}>
              <IconBox $bg={primary.bg} $color={primary.color}><Key size={14} /></IconBox>
              <RowLabel>Wachtwoord wijzigen</RowLabel>
              <Chevron><ChevronRight size={16} /></Chevron>
            </Row>
          </CardGroup>
        )}

        {/* Voorkeuren */}
        <CardGroup>
          <Row $static onClick={toggleSound} style={{ cursor: 'pointer' }}>
            <IconBox $bg={primary.bg} $color={primary.color}><Bell size={14} /></IconBox>
            <RowLabel>Notificaties</RowLabel>
            <ToggleTrack $on={soundEnabled} onClick={toggleSound}>
              <ToggleThumb $on={soundEnabled} />
            </ToggleTrack>
          </Row>
          <Divider />
          <Row onClick={() => setShowThemePicker(true)}>
            <IconBox $bg={primary.bg} $color={primary.color}><Settings size={14} /></IconBox>
            <RowLabel>Weergave</RowLabel>
            <RowValue>{themeLabel}</RowValue>
            <Chevron><ChevronRight size={16} /></Chevron>
          </Row>
          <Divider />
          <Row onClick={() => setShowNavPicker(true)}>
            <IconBox $bg={primary.bg} $color={primary.color}><Navigation size={14} /></IconBox>
            <RowLabel>Navigatie-app</RowLabel>
            <RowValue>{navLabel}</RowValue>
            <Chevron><ChevronRight size={16} /></Chevron>
          </Row>
        </CardGroup>

        {/* Beheer */}
        {canManage && (
          <CardGroup>
            {hasPermission('can_change_product_status') && (
              <>
                <Row onClick={() => navigate('/KMFC/settings/product-status')}>
                  <IconBox $bg={primary.bg} $color={primary.color}><Menu size={14} /></IconBox>
                  <RowLabel>Menu</RowLabel>
                  <Chevron><ChevronRight size={16} /></Chevron>
                </Row>
                <Divider />
              </>
            )}
            <Row onClick={() => navigate('/KMFC/settings/opening-hours')}>
              <IconBox $bg={primary.bg} $color={primary.color}><Clock size={14} /></IconBox>
              <RowLabel>Openingstijden</RowLabel>
              <Chevron><ChevronRight size={16} /></Chevron>
            </Row>
            {canManageUsers && (
              <>
                <Divider />
                <Row onClick={() => navigate('/KMFC/settings/users')}>
                  <IconBox $bg={primary.bg} $color={primary.color}><Users size={14} /></IconBox>
                  <RowLabel>Gebruikers</RowLabel>
                  <Chevron><ChevronRight size={16} /></Chevron>
                </Row>
              </>
            )}
            <Divider />
            <Row onClick={() => setShowArchive(true)}>
              <IconBox $bg={primary.bg} $color={primary.color}><Archive size={14} /></IconBox>
              <RowLabel>Archief bestellingen</RowLabel>
              <Chevron><ChevronRight size={16} /></Chevron>
            </Row>
            {roleId === 1 && (
              <>
                <Divider />
                <Row onClick={() => navigate('/KMFC/settings/banner')}>
                  <IconBox $bg={primary.bg} $color={primary.color}><Megaphone size={14} /></IconBox>
                  <RowLabel>Mededelingen</RowLabel>
                  <Chevron><ChevronRight size={16} /></Chevron>
                </Row>
              </>
            )}
          </CardGroup>
        )}

        {/* Over */}
        <CardGroup>
          <Row $static>
            <IconBox $bg={gray.bg} $color={gray.color}><Info size={14} /></IconBox>
            <RowLabel>Versie</RowLabel>
            <RowValue>{version}</RowValue>
          </Row>
          <Divider />
          <Row onClick={() => navigate('/KMFC/settings/legal')}>
            <IconBox $bg={gray.bg} $color={gray.color}><FileText size={14} /></IconBox>
            <RowLabel>Wettelijke documentatie</RowLabel>
            <Chevron><ChevronRight size={16} /></Chevron>
          </Row>
        </CardGroup>

        {/* Uitloggen */}
        <StandaloneCard $red onClick={() => setShowLogout(true)}>
          <IconBox $bg="rgba(239,68,68,0.12)" $color="#ef4444" style={{ marginRight: 12 }}><LogOut size={14} /></IconBox>
          <span style={{ fontSize: 15, fontWeight: 500, color: '#ef4444' }}>Uitloggen</span>
        </StandaloneCard>
      </Content>

      {toast && <Toast $type={toast.type}>{toast.msg}</Toast>}

      {/* Weergave picker */}
      {showThemePicker && (
        <Overlay onClick={() => setShowThemePicker(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle style={{ marginBottom: 16 }}>Weergave</ModalTitle>
            {THEME_OPTIONS.map(opt => (
              <NavOption key={opt.id} $active={theme === opt.id} onClick={() => { setThemeCtx(opt.id); setShowThemePicker(false); }}>
                <span>{opt.label}</span>
                {theme === opt.id && <span style={{ color: '#22c55e', fontSize: 13 }}>✓</span>}
              </NavOption>
            ))}
          </Modal>
        </Overlay>
      )}

      {/* Navigatie picker */}
      {showNavPicker && (
        <Overlay onClick={() => setShowNavPicker(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle style={{ marginBottom: 16 }}>Navigatie-app</ModalTitle>
            {NAV_OPTIONS.map(opt => (
              <NavOption key={opt.id} $active={navApp === opt.id} onClick={() => selectNavApp(opt.id)}>
                <span>{opt.label}</span>
                {navApp === opt.id && <span style={{ color: '#22c55e', fontSize: 13 }}>✓</span>}
              </NavOption>
            ))}
          </Modal>
        </Overlay>
      )}

      {/* Uitloggen modal */}
      {showLogout && (
        <Overlay onClick={() => setShowLogout(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle>Uitloggen</ModalTitle>
            <ModalText>Weet je zeker dat je wilt uitloggen?</ModalText>
            <ModalButtons>
              <ModalBtn onClick={() => setShowLogout(false)}>Annuleren</ModalBtn>
              <ModalBtn $danger onClick={() => { setShowLogout(false); performLogout(); }}>Uitloggen</ModalBtn>
            </ModalButtons>
          </Modal>
        </Overlay>
      )}

      {showArchive && <ArchiveSheetComponent onClose={() => setShowArchive(false)} />}
    </Container>
  );
}

// ─── Archive Sheet ─────────────────────────────────────────────────────────────

const ArchiveOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 10000;
  @media (min-width: 600px) { align-items: center; }
`;

const sheetUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const ArchiveSheet = styled.div`
  background: ${p => p.theme.colors.surface};
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-width: 480px;
  padding: 20px 20px 40px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: ${sheetUp} 0.28s cubic-bezier(0.16,1,0.3,1) both;
  border: 1px solid ${p => p.theme.colors.border};
  @media (min-width: 600px) { border-radius: 20px; }
`;

const SheetHandle = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'};
  margin: 0 auto 8px;
  @media (min-width: 600px) { display: none; }
`;

const SheetTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  margin: 0 0 4px;
  letter-spacing: -0.02em;
`;

const SheetPeriodCard = styled.div`
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 14px;
  overflow: hidden;
`;

const SheetDateRow = styled.div`
  display: flex;
  align-items: center;
  padding: 0 14px;
  height: 50px;
  gap: 12px;
  & + & { border-top: 1px solid ${p => p.theme.colors.border}; }
`;

const SheetDateLabel = styled.label`
  font-size: 15px;
  color: ${p => p.theme.colors.text};
  width: 110px;
  flex-shrink: 0;
`;

const SheetDateInput = styled.input`
  flex: 1;
  background: none;
  border: none;
  outline: none;
  font-size: 15px;
  font-family: inherit;
  color: ${p => p.theme.colors.text};
  text-align: right;
  cursor: pointer;
  color-scheme: ${p => p.theme.mode};
`;

const SheetInfoCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 12px 13px;
  background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
  border-radius: 12px;
  font-size: 13px;
  color: ${p => p.theme.colors.muted};
  line-height: 1.5;
`;

const SheetExportBtn = styled.button`
  width: 100%;
  padding: 15px;
  background: ${p => p.theme.mode === 'dark' ? '#ffffff' : '#141414'};
  color: ${p => p.theme.mode === 'dark' ? '#000000' : '#ffffff'};
  border: none;
  border-radius: 13px;
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.15s;
  margin-top: 2px;
  &:hover { opacity: 0.85; }
  &:active { opacity: 0.7; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const spinKf = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;
const SpinIcon = styled.span`display:inline-flex;animation:${css`${spinKf} 1s linear infinite`};`;

function ArchiveSheetComponent({ onClose }) {
  const todayStr = new Date().toISOString().split('T')[0];
  const thirtyAgo = new Date(Date.now() - 86400000 * 30).toISOString().split('T')[0];
  const [start, setStart] = useState(thirtyAgo);
  const [end, setEnd] = useState(todayStr);
  const [loading, setLoading] = useState(false);
  const [removeSensitive, setRemoveSensitive] = useState(false);

  const exportCsv = async () => {
    setLoading(true);
    try {
      const { getKmfcClient } = await import('../../lib/supabase');
      const client = getKmfcClient();
      const since = new Date(start).toISOString();
      const before = new Date(new Date(end).getTime() + 86400000).toISOString();
      const { data: ordersData, error } = await client
        .from('orders')
        .select('id,order_code,status,placed_at,customer_name,customer_phone,customer_email,delivery_type,delivery_address,delivery_postcode,delivery_city,delivery_time,is_asap,delivery_notes,interne_notitie,subtotal_cents,delivery_fee_cents,tip_cents,total_cents')
        .gte('placed_at', since).lt('placed_at', before)
        .eq('is_test', false).order('placed_at', { ascending: true }).limit(10000);
      if (error) throw error;

      const ids = ordersData.map(o => o.id);
      let itemsMap = {};
      if (ids.length > 0) {
        const { data: items } = await client.from('order_items').select('order_id,name_snapshot,qty,item_options').in('order_id', ids);
        (items || []).forEach(i => { if (!itemsMap[i.order_id]) itemsMap[i.order_id] = []; itemsMap[i.order_id].push(i); });
      }

      const tz = 'Europe/Amsterdam';
      const fmtTime = iso => iso ? new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', timeZone: tz }) : '';
      const fmtDate = iso => iso ? new Date(iso).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: tz }) : '';
      const fmtEUR = c => `€${((c || 0) / 100).toFixed(2).replace('.', ',')}`;
      const esc = s => { const str = String(s || ''); return (str.includes(';') || str.includes('"') || str.includes('\n')) ? `"${str.replace(/"/g, '""')}"` : str; };

      const headers = ['Ordernummer','Datum','Tijd','Status','Type','Naam','Telefoon','E-mail','Adres','Postcode','Stad','Bezorgtijd','ASAP','Artikelen','Subtotaal','Bezorgkosten','Fooi','Totaal','Notities klant','Interne notitie'];
      const rows = ordersData.map(o => {
        const art = (itemsMap[o.id] || []).map(i => `${i.qty}x ${i.name_snapshot || '?'}${i.item_options ? ` (${i.item_options})` : ''}`).join(' | ');
        return [o.order_code||'',fmtDate(o.placed_at),fmtTime(o.placed_at),o.status||'',o.delivery_type==='delivery'?'Bezorging':'Afhaal',removeSensitive?'':(o.customer_name||''),removeSensitive?'':(o.customer_phone||''),removeSensitive?'':(o.customer_email||''),o.delivery_address||'',o.delivery_postcode||'',o.delivery_city||'',o.delivery_time?fmtTime(o.delivery_time):'',o.is_asap?'Ja':'Nee',art,fmtEUR(o.subtotal_cents),fmtEUR(o.delivery_fee_cents),fmtEUR(o.tip_cents),fmtEUR(o.total_cents),o.delivery_notes||'',o.interne_notitie||''].map(esc).join(';');
      });
      const csv = [headers.map(esc).join(';'), ...rows].join('\n');
      const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `bestellingen-${start}-tm-${end}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export mislukt: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ArchiveOverlay onClick={onClose}>
      <ArchiveSheet onClick={e => e.stopPropagation()}>
        <SheetHandle />
        <SheetTitle>Archief bestellingen</SheetTitle>
        <SheetPeriodCard>
          <SheetDateRow>
            <SheetDateLabel>Van</SheetDateLabel>
            <SheetDateInput type="date" value={start} max={end} onChange={e => setStart(e.target.value)} />
          </SheetDateRow>
          <SheetDateRow>
            <SheetDateLabel>Tot en met</SheetDateLabel>
            <SheetDateInput type="date" value={end} min={start} max={todayStr} onChange={e => setEnd(e.target.value)} />
          </SheetDateRow>
        </SheetPeriodCard>
        <SheetInfoCard>
          <Info size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          Exporteert alle bestellingen in de geselecteerde periode als CSV-bestand. Artikelen worden uitgeschreven.
        </SheetInfoCard>
        <SheetInfoCard as="label" style={{ cursor: 'pointer', gap: 12 }}>
          <input
            type="checkbox"
            checked={removeSensitive}
            onChange={e => setRemoveSensitive(e.target.checked)}
            style={{ width: 16, height: 16, flexShrink: 0, cursor: 'pointer', accentColor: 'currentColor' }}
          />
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Verwijder gevoelige informatie</div>
            <div style={{ fontSize: 12, marginTop: 2, opacity: 0.6 }}>Naam, e-mail en telefoonnummer worden weggelaten</div>
          </div>
        </SheetInfoCard>
        <SheetExportBtn onClick={exportCsv} disabled={loading}>
          {loading ? <SpinIcon><RefreshCw size={15} /></SpinIcon> : <Download size={15} />}
          {loading ? 'Bezig met exporteren…' : 'Download CSV'}
        </SheetExportBtn>
      </ArchiveSheet>
    </ArchiveOverlay>
  );
}
